import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Lock,
  Eye,
  EyeOff,
  Loader2,
  AlertCircle,
  CheckCircle2,
  ArrowLeft,
  ShieldCheck,
  KeyRound,
  ArrowRight,
} from 'lucide-react';
import api from '../api/api';
import styles from './LoginPage.module.css';
import logo from '../assets/images/zone_icon.png';

// Import du nouveau validateur
import PasswordRequirements, { isPasswordFullyValid } from '../components/PasswordRequirements';

// ─────────────────────────────────────────────────────────────
// FEATURES — Panneau gauche
// ─────────────────────────────────────────────────────────────
const FEATURES = [
  {
    Icon: ShieldCheck,
    title: 'Sécurité renforcée',
    desc: 'Votre nouveau mot de passe est chiffré côté serveur',
  },
  {
    Icon: KeyRound,
    title: 'Lien à usage unique',
    desc: 'Le lien expire après utilisation pour votre protection',
  },
];

// ─────────────────────────────────────────────────────────────
// COMPOSANT PRINCIPAL
// ─────────────────────────────────────────────────────────────
const ResetPasswordPage = () => {
  const [searchParams] = useSearchParams();
  const navigate       = useNavigate();
  const token          = searchParams.get('token');

  const [newPassword,     setNewPassword]     = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNew,         setShowNew]         = useState(false);
  const [showConfirm,     setShowConfirm]     = useState(false);
  const [message,         setMessage]         = useState({ type: '', text: '' });
  const [isLoading,       setIsLoading]       = useState(false);
  const [isDone,          setIsDone]          = useState(false);

  // ── Validation en temps réel ──────────────────────────────
  const isPasswordValid = isPasswordFullyValid(newPassword);
  const doPasswordsMatch = newPassword && confirmPassword && newPassword === confirmPassword;
  
  // Le bouton est activé uniquement si tout est au vert
  const canSubmit = isPasswordValid && doPasswordsMatch && !isLoading;

  // ── Submit ────────────────────────────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage({ type: '', text: '' });

    if (!token) {
      setMessage({ type: 'error', text: 'Lien invalide ou manquant.' });
      return;
    }
    // Double sécurité au cas où l'utilisateur force la soumission
    if (!isPasswordValid) {
      setMessage({ type: 'error', text: 'Le mot de passe ne respecte pas les critères de sécurité.' });
      return;
    }
    if (!doPasswordsMatch) {
      setMessage({ type: 'error', text: 'Les mots de passe ne correspondent pas.' });
      return;
    }

    setIsLoading(true);
    try {
      await api.post('/auth/confirm-password-reset', {
        token,
        new_password: newPassword,
      });

      setIsDone(true);
      setMessage({
        type: 'success',
        text: 'Mot de passe modifié avec succès ! Redirection en cours…',
      });

      setTimeout(() => navigate('/'), 2500);
    } catch (err) {
      const msg = err.response?.data?.error ?? 'Le lien a expiré ou est invalide.';
      setMessage({ type: 'error', text: msg });
    } finally {
      setIsLoading(false);
    }
  };

  // ─────────────────────────────────────────────────────────
  return (
    <div className={styles.page}>

      {/* ── Orbes décoratifs ── */}
      <div className={styles.orb1} aria-hidden="true" />
      <div className={styles.orb2} aria-hidden="true" />
      <div className={styles.orb3} aria-hidden="true" />

      {/* ══ CARTE ══ */}
      <div className={styles.card}>

        {/* ── Panneau gauche ── */}
        <aside className={styles.leftPanel} aria-hidden="true">
          <div className={styles.leftInner}>

            <div className={styles.logoWrap}>
              <div className={styles.logoGlow} />
              <img src={logo} alt="CVtoSkill" className={styles.logo} />
            </div>

            <div className={styles.brandText}>
              <h1 className={styles.brandTitle}>
                Sécurisez{' '}
                <span className={styles.brandHighlight}>votre compte</span>
              </h1>
              <p className={styles.brandSub}>
                Choisissez un nouveau mot de passe robuste pour protéger votre espace.
              </p>
            </div>

            <ul className={styles.featureList} role="list">
              {FEATURES.map(({ Icon, title, desc }) => (
                <li key={title} className={styles.featureItem}>
                  <span className={styles.featureIconWrap}>
                    <Icon size={16} strokeWidth={2} />
                  </span>
                  <span className={styles.featureText}>
                    <strong>{title}</strong>
                    <small>{desc}</small>
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </aside>

        {/* ── Panneau droit ── */}
        <main className={styles.rightPanel}>
          <div className={styles.formBox}>
            <section className={styles.formSection}>

              <FormHeader
                title="Nouveau mot de passe"
                subtitle="Choisissez un mot de passe fort et unique"
              />

              {/* Message alerte */}
              {message.text && (
                <Alert type={message.type} message={message.text} />
              )}

              {/* ── Token manquant ── */}
              {!token ? (
                <InvalidToken onBack={() => navigate('/')} />
              ) : isDone ? (
                /* ── Succès ── */
                <SuccessState onRedirect={() => navigate('/')} />
              ) : (
                /* ── Formulaire ── */
                <form onSubmit={handleSubmit} className={styles.form} noValidate>

                  {/* Nouveau mot de passe */}
                  <Field
                    id="newPassword"
                    type={showNew ? 'text' : 'password'}
                    label="Nouveau mot de passe"
                    value={newPassword}
                    onChange={setNewPassword}
                    Icon={Lock}
                    required
                    autoComplete="new-password"
                    suffix={
                      <button
                        type="button"
                        className={styles.eyeBtn}
                        onClick={() => setShowNew((p) => !p)}
                        aria-label={showNew ? 'Masquer' : 'Afficher'}
                      >
                        {showNew
                          ? <EyeOff size={16} strokeWidth={1.75} />
                          : <Eye    size={16} strokeWidth={1.75} />
                        }
                      </button>
                    }
                  />

                  {/* Composant de Checklist de sécurité */}
                  {newPassword && (
                    <PasswordRequirements password={newPassword} />
                  )}

                  {/* Confirmation */}
                  <div style={{ marginTop: '16px' }}>
                    <Field
                      id="confirmPassword"
                      type={showConfirm ? 'text' : 'password'}
                      label="Confirmer le mot de passe"
                      value={confirmPassword}
                      onChange={setConfirmPassword}
                      Icon={Lock}
                      required
                      autoComplete="new-password"
                      suffix={
                        <button
                          type="button"
                          className={styles.eyeBtn}
                          onClick={() => setShowConfirm((p) => !p)}
                          aria-label={showConfirm ? 'Masquer' : 'Afficher'}
                        >
                          {showConfirm
                            ? <EyeOff size={16} strokeWidth={1.75} />
                            : <Eye    size={16} strokeWidth={1.75} />
                          }
                        </button>
                      }
                    />
                  </div>

                  {/* Indicateur de correspondance */}
                  {confirmPassword && (
                    <MatchIndicator match={doPasswordsMatch} />
                  )}

                  <div style={{ marginTop: '24px' }}>
                    <SubmitButton
                      loading={isLoading}
                      label="Modifier le mot de passe"
                      disabled={!canSubmit}
                    />
                  </div>
                </form>
              )}

              {/* Retour login */}
              {!isDone && (
                <div className={styles.textLink} style={{ marginTop: '16px' }}>
                  <button
                    type="button"
                    className={styles.linkBtn}
                    onClick={() => navigate('/')}
                  >
                    <ArrowLeft size={14} strokeWidth={2} />
                    Retour à la connexion
                  </button>
                </div>
              )}

            </section>
          </div>
        </main>

      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// SOUS-COMPOSANTS
// ─────────────────────────────────────────────────────────────

const FormHeader = ({ title, subtitle }) => (
  <div className={styles.formHeader}>
    <h2 className={styles.formTitle}>{title}</h2>
    <p className={styles.formSub}>{subtitle}</p>
  </div>
);

const Field = ({
  id, type, label, value, onChange,
  Icon, required, autoComplete, suffix,
}) => (
  <div className={styles.field}>
    <div className={styles.inputWrap}>
      <span className={styles.inputIcon}>
        <Icon size={16} strokeWidth={1.75} />
      </span>
      <input
        id={id}
        type={type}
        className={styles.input}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder=" "
        required={required}
        autoComplete={autoComplete}
      />
      <label htmlFor={id} className={styles.label}>{label}</label>
      {suffix && <div className={styles.inputSuffix}>{suffix}</div>}
    </div>
  </div>
);

const SubmitButton = ({ loading, label, disabled }) => (
  <button
    type="submit"
    className={`${styles.submitBtn} ${loading ? styles.submitBtnLoading : ''}`}
    disabled={disabled || loading}
    style={{ opacity: disabled ? 0.6 : 1, cursor: disabled ? 'not-allowed' : 'pointer' }}
  >
    {loading ? (
      <>
        <Loader2 size={18} strokeWidth={2} className={styles.spinner} />
        <span>Modification…</span>
      </>
    ) : (
      <>
        <span>{label}</span>
        <ArrowRight size={16} strokeWidth={2.5} />
      </>
    )}
  </button>
);

const Alert = ({ type, message }) => {
  const Icon = type === 'error' ? AlertCircle : CheckCircle2;
  return (
    <div
      className={`${styles.alert} ${styles[`alert--${type}`]}`}
      role="alert"
    >
      <Icon size={16} strokeWidth={2} className={styles.alertIcon} />
      <span>{message}</span>
    </div>
  );
};

const MatchIndicator = ({ match }) => (
  <div
    className={styles.matchIndicator}
    style={{
      color: match ? 'var(--color-success)' : 'var(--color-error)',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      marginTop: '8px',
      fontSize: '0.85rem'
    }}
  >
    {match ? (
      <CheckCircle2 size={14} strokeWidth={2} />
    ) : (
      <AlertCircle size={14} strokeWidth={2} />
    )}
    <span>
      {match ? 'Les mots de passe correspondent' : 'Les mots de passe ne correspondent pas'}
    </span>
  </div>
);

const SuccessState = ({ onRedirect }) => (
  <div className={styles.resetSuccess}>
    <CheckCircle2
      size={52}
      strokeWidth={1.5}
      className={styles.resetIcon}
    />
    <p>Votre mot de passe a été modifié avec succès.</p>
    <button
      type="button"
      className={styles.submitBtn}
      onClick={onRedirect}
      style={{ marginTop: 'var(--space-2)' }}
    >
      <span>Aller à la connexion</span>
      <ArrowRight size={16} strokeWidth={2.5} />
    </button>
  </div>
);

const InvalidToken = ({ onBack }) => (
  <div className={styles.resetSuccess}>
    <AlertCircle
      size={52}
      strokeWidth={1.5}
      style={{ color: 'var(--color-error)' }}
    />
    <p style={{ color: 'var(--color-gray-600)' }}>
      Ce lien est invalide ou a expiré.
      <br />
      Veuillez refaire une demande de réinitialisation.
    </p>
    <button
      type="button"
      className={styles.submitBtn}
      onClick={onBack}
      style={{ marginTop: 'var(--space-2)' }}
    >
      <span>Retour à la connexion</span>
      <ArrowRight size={16} strokeWidth={2.5} />
    </button>
  </div>
);

export default ResetPasswordPage;